import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  AlertCircle,
  User,
  Loader2,
  Clock,
  MapPin,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRegisterSession, Register } from '@/hooks/useRegisterSession';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';

const DENOMINATIONS = [
  { value: 0.01, label: '$0.01' },
  { value: 0.05, label: '$0.05' },
  { value: 0.10, label: '$0.10' },
  { value: 0.25, label: '$0.25' },
  { value: 1.00, label: '$1.00' },
  { value: 5.00, label: '$5.00' },
  { value: 10.00, label: '$10.00' },
  { value: 20.00, label: '$20.00' },
  { value: 50.00, label: '$50.00' },
  { value: 100.00, label: '$100.00' },
  { value: 500.00, label: '$500.00' },
  { value: 1000.00, label: '$1,000.00' },
  { value: 5000.00, label: '$5,000.00' },
];

export default function OpenClose() {
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const { formatCurrency } = useCurrency();
  const {
    activeSession,
    isRegisterOpen,
    isSessionPaused,
    sessionLoading,
    availableRegisters,
    registersInUse,
    registersLoading,
    openRegister,
    isLoading,
  } = useRegisterSession();

  const [selectedRegisterId, setSelectedRegisterId] = useState<string>('');
  const [openingBalance, setOpeningBalance] = useState<string>('');

  const handleOpenRegister = async () => {
    if (!selectedRegisterId) {
      toast.error('Select a register', {
        description: 'Please select a register to open.',
      });
      return;
    }

    const balance = parseFloat(openingBalance) || 0;
    if (balance < 0) {
      toast.error('Invalid balance', {
        description: 'Opening balance cannot be negative.',
      });
      return;
    }

    try {
      await openRegister.mutateAsync({
        registerId: selectedRegisterId,
        openingBalance: balance,
      });

      toast.success('Register opened', {
        description: 'Your register session has been started.',
      });

      setSelectedRegisterId('');
      setOpeningBalance('');
    } catch (error: any) {
      toast.error('Failed to open register', {
        description: error.message || 'An error occurred while opening the register.',
      });
    }
  };

  const handleBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    setOpeningBalance(value);
  };

  if (isLoading || sessionLoading) {
    return (
      <DashboardLayout
        header={
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Open / Close
          </h1>
        }
      >
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (isRegisterOpen || isSessionPaused) {
    return (
      <DashboardLayout
        header={
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Close register
          </h1>
        }
      >
        <p className="text-muted-foreground mb-8">
          Close your register to finalize payments and sales for the day.
        </p>

        <div className="max-w-3xl space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Register details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 text-sm">
                <span className="text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Outlet
                </span>
                <span className="font-medium">{currentLocation?.name || 'Unknown'}</span>

                <span className="text-muted-foreground flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> Register
                </span>
                <span className="font-medium">{activeSession?.register_name || 'Unknown'}</span>

                <span className="text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Opening time
                </span>
                <span className="font-medium">
                  {activeSession?.opened_at
                    ? new Date(activeSession.opened_at).toLocaleString(undefined, {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : 'Unknown'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Count cash</CardTitle>
              <CardDescription>Enter the amount from the till.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <div className="grid grid-cols-3 gap-0 border-b px-4 py-2 bg-muted/50">
                  <span className="text-sm font-medium text-muted-foreground">Denomination</span>
                  <span className="text-sm font-medium text-muted-foreground text-center">Quantity</span>
                  <span className="text-sm font-medium text-muted-foreground text-right">Amount</span>
                </div>
                {DENOMINATIONS.map((denom) => (
                  <DenominationRow
                    key={denom.value}
                    label={denom.label}
                    value={denom.value}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              size="lg"
              onClick={() => {
                if (activeSession?.id) {
                  navigate(`/close-register/${activeSession.id}`);
                }
              }}
            >
              Close Register
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      header={
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Open / Close
        </h1>
      }
    >
      <p className="text-muted-foreground mb-8">
        Open a register to start your shift and begin selling.
      </p>

      <div className="max-w-xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Open Register</CardTitle>
            <CardDescription>
              Select a register and enter your opening cash balance to start your shift.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="register">Select Register</Label>
              {registersLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : availableRegisters.length === 0 ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    No registers available. All registers are currently in use.
                  </p>
                </div>
              ) : (
                <Select value={selectedRegisterId} onValueChange={setSelectedRegisterId}>
                  <SelectTrigger id="register">
                    <SelectValue placeholder="Choose a register..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRegisters.map((register: Register) => (
                      <SelectItem key={register.id} value={register.id}>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span>{register.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {registersInUse.length > 0 && (
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                  Currently in use
                </Label>
                <div className="space-y-1">
                  {registersInUse.map((register: Register) => (
                    <div
                      key={register.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{register.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {register.current_user_name}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          In use
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="opening-balance">Opening Cash Balance</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="opening-balance"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={openingBalance}
                  onChange={handleBalanceChange}
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the amount of cash in the drawer when starting your shift.
              </p>
            </div>

            <Button
              className="w-full"
              onClick={handleOpenRegister}
              disabled={
                !selectedRegisterId || openRegister.isPending || availableRegisters.length === 0
              }
            >
              {openRegister.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                <>
                  <DollarSign className="mr-2 h-4 w-4" />
                  Open Register
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function DenominationRow({
  label,
  value,
  formatCurrency,
}: {
  label: string;
  value: number;
  formatCurrency: (n: number) => string;
}) {
  const [quantity, setQuantity] = useState(0);
  const amount = quantity * value;

  return (
    <div className="grid grid-cols-3 items-center gap-0 border-b last:border-b-0 px-4 py-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex justify-center">
        <Input
          type="number"
          min={0}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
          className="w-20 text-center"
        />
      </div>
      <span className="text-sm text-right">{formatCurrency(amount)}</span>
    </div>
  );
}
